import { Node, mergeAttributes, nodePasteRule, type JSONContent, type PasteRuleMatch } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { Suggestion, type SuggestionOptions } from "@tiptap/suggestion";
import { SvelteNodeViewRenderer } from "svelte-tiptap";
import DataTokenComponent from "#lib/components/content-editors/extensions/DataToken.svelte";
import { findSuggestionMatch } from "#lib/components/content-editors/extensions/suggestionMatcher.js";
import type { SubstitutionToken, TokenProvider } from "#lib/tokenProvider.js";

// See `addAttributes` below

interface DataTokenOptions<SuggestionItem = unknown> {
	/**
	 * The HTML attributes for a dataToken node.
	 * @default {}
	 * @example { class: 'foo' }
	 */
	HTMLAttributes: Record<string, string>;

	provider?: TokenProvider;

	// If true, let the dropdown evaluate a match, if false keep it hidden
	extraAllowCondition: () => boolean;

	/**
	 * The suggestion options.
	 * @default {}
	 * @example { char: '@', pluginKey: DataTokenPluginKey, command: ({ editor, range, props }) => { ... } }
	 */
	suggestion: Omit<SuggestionOptions<SuggestionItem>, "editor"> & {
		anchor?: HTMLElement;
	};
}
declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		DataToken: {
			insertToken: (token: SubstitutionToken) => ReturnType;
		};
	}
}

/**
 * The plugin key for the dataToken plugin.
 * @default 'data-token'
 */
const DataTokenPluginKey = new PluginKey("data-token");

/**
 * This extension allows you to insert dataTokens into the editor.
 * @see https://www.tiptap.dev/api/extensions/dataToken
 */
export const DataToken = Node.create<DataTokenOptions>({
	name: "data-token",
	addNodeView() {
		return SvelteNodeViewRenderer(DataTokenComponent);
	},
	addCommands() {
		return {
			insertToken:
				(token: SubstitutionToken) =>
				({ commands }) => {
					if (token) {
						return commands.insertContent({
							type: "data-token",
							attrs: {
								label: token.label,
								value: token.value,
								provider: token.provider,
							},
						});
					}
					return false;
				},
		};
	},
	addOptions() {
		return {
			disableSpace: false,
			HTMLAttributes: {},
			extraAllowCondition: () => true,
			suggestion: {
				char: "",
				findSuggestionMatch,
				pluginKey: DataTokenPluginKey,
				command: ({ editor, range, props }) => {
					// increase range.to by one when the next node is of type "text"
					// and starts with a space character
					const nodeAfter = editor.view.state.selection.$to.nodeAfter;
					const overrideSpace = nodeAfter?.text?.startsWith(" ");

					if (overrideSpace) {
						range.to += 1;
					}
					const tokens = [{ type: this.name, attrs: props }] as JSONContent[];
					if (!props?.disableSpace) {
						tokens.push({ type: "text", text: " " });
					}
					editor.chain().focus(range.to).insertContentAt(range, tokens).run();

					window.getSelection()?.collapseToEnd();
				},
			},
		};
	},
	group: "inline",
	inline: true,
	selectable: false,
	atom: true,
	addAttributes() {
		return {
			label: {
				default: null,
				parseHTML: element => element.getAttribute("data-label"),
				renderHTML: attributes => (attributes.label ? { "data-label": attributes.label } : {}),
			},
			value: {
				default: null,
				parseHTML: element => element.getAttribute("data-value"),
				renderHTML: attributes => (attributes.value ? { "data-value": attributes.value } : {}),
			},
			provider: {
				default: null,
				parseHTML: element => element.getAttribute("data-provider"),
				renderHTML: attributes => (attributes.provider ? { "data-provider": attributes.provider } : {}),
			},
		};
	},
	addPasteRules() {
		return [
			nodePasteRule({
				find: (text: string): PasteRuleMatch[] | null | undefined => {
					if (!this.options.provider) return;
					const provider = this.options.provider;
					const tokens = provider.parse(text).filter(token => provider.instanceofSubstitutionToken(token));
					return tokens.map(token => ({ index: token.index!, text: token.value, data: { label: token.label, value: token.value } }));
				},
				type: this.type,
				getAttributes: match => {
					return match.data;
				},
			}),
		];
	},
	parseHTML() {
		return [
			{
				tag: `span[data-type="${this.name}"]`,
			},
		];
	},
	renderHTML({ node, HTMLAttributes }) {
		const attrs = mergeAttributes({ "data-type": this.name }, this.options.HTMLAttributes, HTMLAttributes);
		return ["span", attrs, node.attrs.value];
	},
	renderText({ node }) {
		return node.attrs.value;
	},
	addKeyboardShortcuts() {
		return {
			Backspace: () =>
				this.editor.commands.command(({ tr, state }) => {
					let isDataToken = false;
					const { selection } = state;
					const { empty, anchor } = selection;

					if (!empty) {
						return false;
					}

					state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
						if (node.type.name === this.name) {
							isDataToken = true;
							tr.insertText("", pos, pos + node.nodeSize);

							return false;
						}
					});
					return isDataToken;
				}),
		};
	},
	addProseMirrorPlugins() {
		return [
			Suggestion({
				editor: this.editor,
				allow: ({ state, range }) => {
					if (!this.options.extraAllowCondition()) return false;
					const $from = state.doc.resolve(range.from);
					const type = state.schema.nodes[this.name];
					const allow = !!$from.parent.type.contentMatch.matchType(type);
					return allow;
				},
				...this.options.suggestion,
			}),
		];
	},
});
